import { useEffect, useRef } from 'react';
import { tradingViewLocale, useT } from '@/i18n';

// TradingView's free Advanced Chart widget. The attribution block it renders must stay (design.md §8).
//
// Sizing: with `autosize` the embed script rewrites the container's inline height to "100%", so the chart is
// exactly as tall as the nearest ancestor with a definite height. This component therefore owns a positioned
// frame (sized by the caller through `className`/`style`) and lets the widget fill it absolutely; without that
// the iframe collapses to the browser default of 150px.
export function TradingViewChart({ symbol, className, style }: { symbol: string; className?: string; style?: React.CSSProperties }) {
  const container = useRef<HTMLDivElement>(null);
  const { locale } = useT();
  const tvLocale = tradingViewLocale(locale);

  useEffect(() => {
    const el = container.current;
    if (!el) return;
    el.innerHTML = '';
    const widget = document.createElement('div');
    widget.className = 'tradingview-widget-container__widget';
    widget.style.cssText = 'height: calc(100% - 32px); width: 100%;';
    // Built here rather than in JSX: the script looks for this block to reserve its 32px, and clearing the
    // container above would otherwise drop a React-rendered one.
    const copyright = document.createElement('div');
    copyright.className = 'tradingview-widget-copyright';
    copyright.style.cssText = 'height: 32px; box-sizing: border-box; padding: 8px 12px; font-size: 11px; line-height: 16px; color: var(--muted);';
    const link = document.createElement('a');
    link.href = `https://www.tradingview.com/symbols/${symbol.replace(':', '-')}/`;
    link.rel = 'noopener nofollow';
    link.target = '_blank';
    link.textContent = 'Charts and quotes by TradingView';
    copyright.appendChild(link);
    const script = document.createElement('script');
    script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js';
    script.async = true;
    script.type = 'text/javascript';
    script.text = JSON.stringify({
      autosize: true,
      symbol,
      interval: 'D',
      timezone: 'America/New_York',
      theme: 'light',
      style: '3',
      locale: tvLocale,
      backgroundColor: '#fbf8f2',
      gridColor: 'rgba(217, 208, 193, 0.5)',
      hide_top_toolbar: false,
      hide_legend: false,
      allow_symbol_change: false,
      save_image: false,
      calendar: false,
      support_host: 'https://www.tradingview.com',
    });
    el.append(widget, copyright, script);
    return () => {
      el.innerHTML = '';
    };
  }, [symbol, tvLocale]);

  return (
    <div className={className} style={{ position: 'relative', ...style }}>
      <div className="tradingview-widget-container" ref={container} style={{ position: 'absolute', inset: 0 }} />
    </div>
  );
}
